import { useState, useEffect } from "react";
import { Users, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import {
  getMiningUsersAPI,
  getMiningUserDetailsAPI,
  updateMiningUserStatusAPI,
} from "../../api/miningApi";
import { MiningUserDetailsModal } from "../../components/MiningUserDetailsModal";

export default function MiningActiveUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [userId, setUserId] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("mining_access_token");
    if (!token) {
      setError("No authentication token found. Please login first.");
      setLoading(false);
      return;
    }
    fetchUsers();
  }, [page, pageSize, search, userId]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("mining_access_token");
      if (!token) throw new Error("No authentication token found. Please login first.");

      const response = await getMiningUsersAPI(page, pageSize, search, userId);

      let users = [];
      let totalPages = 1;
      let totalUsers = 0;

      if (response && response.data) {
        users = response.data.users || response.data.data || [];
        totalPages = response.data.totalPages || response.data.total_pages || 1;
        totalUsers = response.data.totalUsers || response.data.total_users || response.data.total || 0;
      } else if (response && Array.isArray(response)) {
        users = response;
        totalUsers = response.length;
      } else if (response && response.users) {
        users = response.users;
        totalPages = response.totalPages || 1;
        totalUsers = response.totalUsers || 0;
      }

      setUsers(users);
      setTotalPages(totalPages);
      setTotalUsers(totalUsers);
    } catch (err) {
      setError(err.message || "Failed to fetch users");
      setUsers([]);
      setTotalPages(1);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
    setDetailsLoading(true);
    setUserDetails(null);
    try {
      const response = await getMiningUserDetailsAPI(user.userId);
      if (response.success) {
        setUserDetails({
          ...response.data,
          appOpens: user.appOpens || response.data.appOpens || [],
          miningStops: user.miningStops || response.data.miningStops || [],
        });
      } else {
        setUserDetails(null);
      }
    } catch (error) {
      setUserDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setUserDetails(null);
  };

  const handleStatusToggle = async (userId, newStatus) => {
    try {
      await updateMiningUserStatusAPI(userId, newStatus);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.userId === userId ? { ...user, accountStatus: newStatus } : user
        )
      );
      if (userDetails && userDetails.userId === userId) {
        setUserDetails((prev) => ({ ...prev, accountStatus: newStatus }));
      }
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const renderPagination = () => {
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);
    return pages.map((pageNum) => {
      if (
        pageNum === 1 ||
        pageNum === totalPages ||
        (pageNum >= page - 1 && pageNum <= page + 1)
      ) {
        return (
          <button
            key={pageNum}
            onClick={() => setPage(pageNum)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              pageNum === page
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {pageNum}
          </button>
        );
      }
      if (pageNum === page - 2 || pageNum === page + 2) {
        return (
          <span key={pageNum} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-400 text-base mb-4">{error}</p>
          <button
            onClick={() => fetchUsers()}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
          Active Users <span className="text-gradient-ginox">Management</span>
        </h1>
        <p className="text-sm text-muted-foreground">Manage and monitor active user accounts</p>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Search
            </label>
            <input
              type="text"
              placeholder="name, username"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-48 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              User ID
            </label>
            <input
              type="text"
              placeholder="userID"
              value={userId}
              onChange={(e) => { setUserId(e.target.value); setPage(1); }}
              className="w-40 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page
            </label>
            <select
              value={page.toString()}
              onChange={(e) => setPage(parseInt(e.target.value))}
              className="w-16 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: Math.min(totalPages, 50) }, (_, i) => {
                let pageNum;
                if (totalPages <= 50) {
                  pageNum = i + 1;
                } else {
                  const start = Math.max(1, page - 25);
                  pageNum = start + i;
                  if (pageNum > totalPages) return null;
                }
                return (
                  <option key={pageNum} value={pageNum.toString()}>{pageNum}</option>
                );
              }).filter(Boolean)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              value={pageSize.toString()}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
              className="w-20 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["Name", "User ID", "Status", "Mining Count", "Created At", "Platform Username", "Actions"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : users.length > 0 ? (
              users.map((user) => (
                <tr key={user._id} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    <div className="flex items-center gap-3">
                      {user.preferences?.avatar ? (
                        <img src={user.preferences.avatar} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-ginox-green/20 border border-ginox-green/30 flex items-center justify-center text-ginox-green text-xs font-medium">
                          {user.name?.split(" ").map((n) => n[0]).join("") || "?"}
                        </div>
                      )}
                      <span>{user.username}</span>
                    </div>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{user.userId}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      user.accountStatus === "Active"
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : "bg-red-500/10 text-red-400 border border-red-500/20"
                    }`}>
                      {user.accountStatus}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">{user.miningCount}</td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {user.preferences?.platformUsername || (
                      <span className="text-muted-foreground/50 italic">null</span>
                    )}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <button
                      onClick={() => handleViewDetails(user)}
                      className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors flex items-center gap-1.5"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      More Info
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(totalPages > 1 || loading) && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            {loading ? "Loading..." : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(page * pageSize, totalUsers)} of ${totalUsers} users`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {!loading && renderPagination()}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      <MiningUserDetailsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        userDetails={userDetails}
        loading={detailsLoading}
        onStatusToggle={handleStatusToggle}
      />
    </div>
  );
}
